import {CodeInPlaceLink} from './CodeInPlaceLink';
import {LandingNav} from 'landing/components/LandingNav';
// Shared webapp footer (nav links + Stanford-mandated legal footer), reused
// verbatim so PAI's team page carries the same legal footer as the rest of the
// site. See the note in the splash looks for why cross-app source imports of
// webapp internals are established PAI precedent.
import {Footer} from 'landing/components/Footer';
import {getDownloadURL, getStorage, ref} from 'firebase/storage';
import {useEffect, useState} from 'react';
import styled from 'styled-components';

/**
 * A person featured on the PAI team page.
 */
interface TeamMember {
  /** Display name, shown under the photo and used as the image alt text. */
  name: string;
  /** Role/title shown beneath the name. */
  role: string;
  /**
   * The photo, as either a Storage object name under `teamPhotos/` or an
   * absolute public-root path. See {@link useTeamPhotoUrl} for which is which.
   */
  img: string;
  /** Personal website the photo and name link to (opens in a new tab). Optional; omitted renders an unlinked card. */
  href?: string;
}

/**
 * PAI's core team. Photos live in webapp's `public/people/` (PAI's Vite
 * `publicDir` points there — see `vite.config.ts`).
 */
const CORE: TeamMember[] = [
  {
    name: 'Chris Piech',
    role: 'Instructor',
    img: '/people/chris.jpg',
    href: 'https://stanford.edu/~cpiech/bio/index.html',
  },
  {
    name: 'Mehran Sahami',
    role: 'Instructor',
    img: '/people/mehran.jpg',
    href: 'https://profiles.stanford.edu/mehran-sahami',
  },
  {
    name: 'Kelly Cochran',
    role: '',
    img: '/people/kelly-cochran.jpg',
  },
  {
    name: 'Juliette Woodrow',
    role: 'Founding Team',
    img: '/people/juliette-woodrow.jpg',
    href: 'https://juliettewoodrow.github.io/',
  },
  {
    name: 'Sierra Wang',
    role: 'Founding Team',
    img: '/people/sierra-wang.jpg',
  },
  {
    name: 'Cameron Mohne',
    role: '',
    img: '/people/cameron-mohne.jpg',
    href: 'https://icl0ud.github.io/',
  },
  {
    name: 'Mitchell Estberg',
    role: '',
    img: '/people/mitchell-estberg.jpg',
    href: 'https://mitchellestberg.com/',
  },
];

/**
 * PAI's head teaching assistants, rendered in the same grid as {@link CORE} and
 * distinguished from it only by the role line under each name.
 *
 * Photos are Storage object names rather than committed files, so adding a TA
 * here also means uploading `teamPhotos/<file>` to the bucket.
 */
const HEAD_TEACHING_ASSISTANTS: TeamMember[] = [
  {
    name: 'Lilian Naing Chen',
    role: 'Head Teaching Assistant',
    img: 'lilian-naing-chen.jpg',
  },
  {
    name: 'Bob Moll',
    role: 'Head Teaching Assistant',
    img: 'bob-moll.jpg',
  },
];

/**
 * PAI team page, registered as CIP's `TeamPage` override (see `main.tsx`) so the
 * shared footer's "Team" link resolves to PAI's team rather than the CIP team.
 *
 * Mirrors the CIP team page's outer structure (`LandingNav` + a narrow content
 * container + the shared {@link Footer}) so it reads as part of the same site,
 * with PAI-specific copy and instructors.
 *
 * @return The rendered PAI team page.
 * @throws Never.
 */
export function PaiTeamPage() {
  return (
    <div className="pageOuter">
      <div>
        <LandingNav applicationType={null} subpage={null} alwaysShowHome />
        <div className="container-narrow container mb-5">
          <div className="row">
            <div className="col">
              <h1 className="mt-4">The Probability for AI Team</h1>

              <p>
                Probability for AI is a free Stanford course taught the{' '}
                <CodeInPlaceLink /> way — by a community of instructors,
                developers, and volunteer section leaders working to make the
                mathematics behind modern AI accessible to learners everywhere.
              </p>

              <PhotoGrid>
                {[...CORE, ...HEAD_TEACHING_ASSISTANTS].map(member => (
                  <MemberCard key={member.name} member={member} />
                ))}
              </PhotoGrid>
            </div>
          </div>
        </div>
      </div>
      {/* Rendered outside the content container so its own `landing.scss`
          styling (Bootstrap grid + list spacing) applies as it does on the main
          webapp landing pages. */}
      <Footer brandName="PAI" />
    </div>
  );
}

/**
 * Single team-member card: a photo above the person's name and role. When the
 * member has an `href`, the photo and name link to it in a new tab; otherwise
 * the card is unlinked. Until the photo resolves the frame renders empty, so
 * its background colour stands in for the image.
 *
 * @param member - The team member to render (name, role, image, optional link).
 * @return The rendered member card.
 * @throws Never.
 */
function MemberCard({member}: {member: TeamMember}) {
  const {name, role, img, href} = member;
  const photoUrl = useTeamPhotoUrl(img);
  const photo = (
    <PhotoFrame>
      {photoUrl === null ? null : <img src={photoUrl} alt={name} />}
    </PhotoFrame>
  );
  return (
    <Card>
      {href ? (
        <PhotoLink href={href} target="_blank" rel="noreferrer">
          {photo}
        </PhotoLink>
      ) : (
        photo
      )}
      <Name>
        {href ? (
          <a href={href} target="_blank" rel="noreferrer">
            {name}
          </a>
        ) : (
          name
        )}
      </Name>
      <Role>{role}</Role>
    </Card>
  );
}

/**
 * Resolves a {@link TeamMember.img} value to an `<img>` src.
 *
 * A value beginning with `/` is a file committed under webapp's `public/` and
 * is returned as-is. Anything else is an object name under Cloud Storage's
 * `teamPhotos/` prefix — the same prefix CIP's team page reads from, so a photo
 * already uploaded for CIP does not need a second copy committed here. That
 * prefix grants unconditional read (see the storage rules for the default
 * bucket), so it resolves for signed-out visitors too.
 *
 * Each card is keyed by member name over a module-level roster, so a given
 * mount's `img` never changes; the Storage result therefore needs no reset
 * between values.
 *
 * @param img - The member's `img` value, in either of the two forms above.
 * @return The resolved src, or `null` while a Storage lookup is outstanding or
 *   after one failed. Callers render the empty frame in that case.
 * @throws Never. A failed lookup is logged and leaves the result `null`.
 */
function useTeamPhotoUrl(img: string): string | null {
  const isStaticPath = img.startsWith('/');
  const [storageUrl, setStorageUrl] = useState<string | null>(null);

  useEffect(() => {
    if (isStaticPath) {
      return;
    }
    let isCurrent = true;
    getDownloadURL(ref(getStorage(), `teamPhotos/${img}`))
      .then(resolved => {
        if (!isCurrent) {
          return;
        }
        setStorageUrl(resolved);
      })
      .catch(error => {
        console.debug('[PaiTeamPage] could not resolve team photo', img, error);
      });
    return () => {
      isCurrent = false;
    };
  }, [img, isStaticPath]);

  return isStaticPath ? img : storageUrl;
}

const PhotoGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 2.5rem;
  margin-top: 2rem;
`;

const Card = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 0.5rem;
  width: 168px;
`;

const PhotoFrame = styled.div`
  width: 168px;
  height: 168px;
  border-radius: 20px;
  overflow: hidden;
  background-color: #80221c;
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.14);

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
`;

const PhotoLink = styled.a`
  display: block;
`;

const Name = styled.div`
  font-size: 1rem;
  font-weight: 600;
  color: #1d1d1f;
`;

const Role = styled.div`
  font-size: 0.9rem;
  color: #6e6e73;
`;
